<template>
  <div class="prospection-stats bento-item" role="region" aria-label="Suivi de la prospection">
    <div class="stats-head">
      <h3>Suivi de la prospection</h3>
      <span class="stats-sub">{{ prospects.length }} prospect{{ prospects.length > 1 ? 's' : '' }} en cours</span>
    </div>

    <div class="funnel">
      <template v-for="(stage, index) in stages" :key="stage.value">
        <span v-if="index > 0" class="funnel-arrow" aria-hidden="true">&rsaquo;</span>
        <div class="funnel-step">
          <span class="funnel-count">{{ stage.count }}</span>
          <span class="funnel-label">{{ stage.label }}</span>
          <span class="funnel-bar" aria-hidden="true"><i :style="{width: barWidth(stage.count) + '%'}"></i></span>
        </div>
      </template>
      <div class="kpis">
        <div class="kpi" :class="{warn: toRelaunch.length > 0}" :title="'Prospects jamais contactés ou sans contact depuis ' + RELAUNCH_DAYS + ' jours'">
          <span class="kpi-count">{{ toRelaunch.length }}</span>
          <span class="kpi-label">à relancer</span>
        </div>
        <div class="kpi" :class="{danger: neverContacted > 0}" title="Prospects pour lesquels aucun contact n'est enregistré">
          <span class="kpi-count">{{ neverContacted }}</span>
          <span class="kpi-label">jamais contactés</span>
        </div>
        <div class="kpi" title="Clients acquis sur l'ensemble des prospects clôturés (acquis ou non intéressés)">
          <span class="kpi-count">{{ conversionRate === undefined ? '-' : conversionRate + ' %' }}</span>
          <span class="kpi-label">de conversion</span>
        </div>
      </div>
    </div>

    <div v-if="toRelaunch.length > 0" class="relaunch">
      <p class="relaunch-title">À relancer en priorité</p>
      <ul>
        <li v-for="client in priority" :key="client.id">
          <button type="button" class="relaunch-row" @click="open(client)">
            <span class="relaunch-name">{{ client.structuredInformations && client.structuredInformations.name }}</span>
            <StatusBadge :status="client.status" :label="statusLabel(client.status)" />
            <span class="relaunch-date">{{ client.lastContact ? 'Dernier contact ' + timeAgo(client.lastContact).toLowerCase() : 'Jamais contacté' }}</span>
          </button>
        </li>
      </ul>
      <p v-if="toRelaunch.length > priority.length" class="relaunch-more">et {{ toRelaunch.length - priority.length }} autre{{ toRelaunch.length - priority.length > 1 ? 's' : '' }} dans le tableau ci-dessous</p>
    </div>
    <p v-else-if="loaded" class="relaunch-empty">Aucun prospect à relancer : tous ont été contactés depuis moins de {{ RELAUNCH_DAYS }} jours.</p>
  </div>
</template>

<script>
import {ClientController} from "@/controllers/business/ClientController";
import {CLIENT_STATUS_ENUM} from "@/models/clientStatusEnum";
import {ACTIVATION_STATUS_ENUM} from "@/models/activationStatusEnum";
import {PROSPECTION_STATUSES} from "@/components/clientTabs";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {timeAgo} from "@/utils";

const DAY = 24 * 60 * 60 * 1000;

/**
 * Bloc de suivi de la prospection : entonnoir par statut, prospects à relancer et taux de conversion.
 * Les chiffres portent sur tous les clients (indépendamment des filtres du tableau) ; load() les recalcule.
 * callback(client) est appelé au clic sur un prospect à relancer.
 */
export default {
  name: 'ClientProspectionStats',
  components: {StatusBadge},
  props: ['callback'],
  data() {
    return {
      RELAUNCH_DAYS: 15,
      clients: [],
      loaded: false,
    };
  },
  computed: {
    prospects() {
      return this.clients.filter(client => PROSPECTION_STATUSES.includes(client.status));
    },
    stages() {
      return PROSPECTION_STATUSES.map(status => ({
        value: status,
        label: CLIENT_STATUS_ENUM[status].label,
        count: this.prospects.filter(client => client.status === status).length,
      }));
    },
    // Prospects jamais contactés ou pas depuis RELAUNCH_DAYS jours : les plus anciens d'abord
    toRelaunch() {
      const limit = Date.now() - this.RELAUNCH_DAYS * DAY;
      const since = client => client.lastContact ? new Date(client.lastContact).getTime() : new Date(client.createdAt).getTime() - DAY * 100000;
      return this.prospects
          .filter(client => !client.lastContact || new Date(client.lastContact).getTime() < limit)
          .sort((a, b) => since(a) - since(b));
    },
    priority() {
      return this.toRelaunch.slice(0, 5);
    },
    neverContacted() {
      return this.prospects.filter(client => !client.lastContact).length;
    },
    conversionRate() {
      const converted = this.clients.filter(client => client.status === CLIENT_STATUS_ENUM.CONVERTED.value).length;
      const lost = this.clients.filter(client => client.status === CLIENT_STATUS_ENUM.UNINTERESTED.value).length;
      return converted + lost > 0 ? Math.round(converted * 100 / (converted + lost)) : undefined;
    },
  },
  methods: {
    timeAgo,
    async load() {
      const resp = await ClientController.getAll("");
      // Les clients archivés ne comptent ni dans l'entonnoir ni dans les relances
      if (resp && !resp.errors) this.clients = resp.filter(client => client.state !== ACTIVATION_STATUS_ENUM.DEACTIVATED.value);
      this.loaded = true;
    },
    // Largeur de la jauge d'une étape, relative à l'étape la plus remplie (minimum visible si non nulle)
    barWidth(count) {
      const max = Math.max(...this.stages.map(stage => stage.count), 0);
      return max > 0 && count > 0 ? Math.max(Math.round(count * 100 / max), 8) : 0;
    },
    statusLabel(status) {
      return CLIENT_STATUS_ENUM[status] ? CLIENT_STATUS_ENUM[status].label : undefined;
    },
    open(client) {
      if (this.callback) this.callback(client);
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.prospection-stats
  // Tuile bento : le style vient de .section-content > .bento-item (public.global.styl)
  margin-bottom 20px

.stats-head
  display flex
  align-items center
  flex-wrap wrap
  gap 10px 12px
  margin-bottom 16px

  h3
    margin 0
    font-size 1.05em
    text-transform uppercase
    color THEME_COLOR_PRIMARY

  .stats-sub
    padding 3px 10px
    border-radius 999px
    background #E4EEFC
    color #2F5FA8
    font-size 0.78em
    font-weight 700

// Code couleur : bleu = information neutre, orange/rouge = travail à faire, citron réservé à l'état courant / aux actions
// ---- Entonnoir : une carte par étape, jauge relative à l'étape la plus remplie
.funnel
  display flex
  align-items stretch
  flex-wrap wrap
  gap 10px

.funnel-step, .kpi
  display flex
  flex-direction column
  justify-content center
  gap 2px
  min-width 112px
  padding 12px 16px
  border 1px solid #D6E3F5
  border-radius 14px
  background-image linear-gradient(160deg, #FAFCFF, #EDF3FD)
  transition box-shadow .15s ease, transform .15s ease

  &:hover
    box-shadow 0 4px 14px rgba(47, 95, 168, 0.15)
    transform translateY(-1px)

.funnel-count, .kpi-count
  font-size 1.8em
  font-weight 800
  line-height 1.1
  color #1F3A63

.funnel-label, .kpi-label
  font-size 0.8em
  font-weight 600
  color #5A6F8F

.funnel-bar
  display block
  height 4px
  margin-top 8px
  border-radius 999px
  background rgba(47, 95, 168, 0.14)
  overflow hidden

  i
    display block
    height 100%
    border-radius 999px
    background #5B8DD6

.funnel-arrow
  align-self center
  font-size 1.6em
  color #B5C9E8

// ---- Indicateurs : neutres en bleu, orange puis rouge quand il y a quelque chose à faire
.kpis
  display flex
  flex-wrap wrap
  gap 10px
  margin-left auto

.kpi
  &.warn
    border-color #F5CF9A
    background-image linear-gradient(160deg, #FFFCF7, #FFF0DA)

    .kpi-count
      color #C25E00

    .kpi-label
      color #8C5A1E

  &.danger
    border-color #F4B1A4
    background-image linear-gradient(160deg, #FFFBFA, #FFEDE8)

    .kpi-count
      color #C2381F

    .kpi-label
      color #8F3A2B

// ---- Prospects à relancer
.relaunch
  margin-top 18px
  padding-top 16px
  border-top 1px dashed #C9D8EE

  ul
    display flex
    flex-direction column
    gap 6px
    margin 0
    padding 0
    list-style none

.relaunch-title
  margin 0 0 10px
  font-size 0.78em
  font-weight 800
  letter-spacing 0.06em
  text-transform uppercase
  color #2F5FA8

.relaunch-row
  display flex
  align-items center
  gap 12px
  width 100%
  padding 9px 12px
  border 1px solid #D6E3F5
  border-radius 10px
  background #F5F9FF
  text-align left
  font inherit
  cursor pointer
  transition background-color .15s ease, border-color .15s ease

  &:hover
    background #E4EEFC
    border-color #9DBBE6

  .relaunch-name
    flex 0 1 auto
    font-weight 700
    color #1F3A63
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

  .relaunch-date
    margin-left auto
    color #5A6F8F
    font-size 0.85em
    white-space nowrap

.relaunch-more
  margin 10px 0 0
  color #5A6F8F
  font-size 0.85em

.relaunch-empty
  margin 16px 0 0
  padding 10px 14px
  border-radius 10px
  background #E9F6EC
  color #1f6a2c
  font-size 0.88em
  font-weight 600

@media (max-width 700px)
  .funnel-arrow
    display none

  .kpis
    margin-left 0
    width 100%
</style>
